import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Table, TableCell, TableBody, TableRow, TableHead, Grid } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import EditIcon from '@mui/icons-material/Edit';
import { Button, Card, Dialog, IconButton, Typography } from '@totalsoft/rocket-ui';
import { BoxActionsWrapper } from 'components/common/CommonStyles';

export default function SavedDocuments({ savedDocuments, onRemove, onPreview, onEdit }) {
  const [open, setOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);

  const handleOpenDialog = id => () => {
    setItemToDelete(id);
    setOpen(true);
  };

  const handleCloseDialog = () => {
    setItemToDelete(null);
    setOpen(false);
  };

  const handleOnRemove = id => () => {
    onRemove(id);
    handleCloseDialog();
  };

  const handleOnView = id => () => onPreview(id);
  const handleOnEdit = id => () => onEdit(id);
  const { t } = useTranslation();

  const isPdf = doc => {
    return doc.pages[0].item.type === 'PAGE' || doc.pages[0].item.fileName.endsWith('.pdf');
  };

  const getFileOrPages = doc => {
    if (doc.pages[0].item.type === 'PAGE') return doc.pages.length;

    return doc.pages[0].item.fileName;
  };

  return (
    <Card
      title={
        <Grid container alignItems='center' gap={1}>
          <CheckCircleIcon color='success' />
          <Typography variant='subtitle1' color='textPrimary'>
            {t('BulkPdf.SavedDocumentTitle')}
          </Typography>
        </Grid>
      }
    >
      <Table size='small'>
        <TableHead>
          <TableRow>
            <TableCell>
              <strong>{`${t('BulkPdf.NewDocumentType')} (${t('BulkPdf.NewDocumentEntity')})`}</strong>
            </TableCell>
            <TableCell>
              <strong>{t('BulkPdf.PagesOrFile')}</strong>
            </TableCell>
            <TableCell></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {Object.keys(savedDocuments).map(key => (
            <TableRow key={key}>
              <TableCell>{savedDocuments[key].attachment?.attachmentName}</TableCell>
              <TableCell style={{ maxWidth: '100px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {getFileOrPages(savedDocuments[key])}
              </TableCell>
              <TableCell>
                <BoxActionsWrapper justifyContent='end'>
                  {isPdf(savedDocuments[key]) && (
                    <IconButton onClick={handleOnView(key)} variant='text' color='primary' size='small'>
                      <PictureAsPdfIcon />
                    </IconButton>
                  )}
                  <IconButton onClick={handleOnEdit(key)} variant='text' color='primary' size='small'>
                    <EditIcon />
                  </IconButton>
                  <IconButton onClick={handleOpenDialog(key)} variant='text' color='primary' size='small'>
                    <CloseIcon />
                  </IconButton>
                </BoxActionsWrapper>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Dialog
        open={open}
        onClose={handleCloseDialog}
        aria-labelledby='delete-document-actions-dialog-title'
        fullWidth
        maxWidth={'sm'}
        transitionDuration={0}
        scroll='body'
        title={t('BulkPdf.Dialog.DeleteDocumentTitle')}
        content={t('BulkPdf.Dialog.DeleteDocumentSubtitle')}
        actions={
          <BoxActionsWrapper forDialog>
            <Button variant='outlined' onClick={handleCloseDialog}>
              {t('Cancel')}
            </Button>
            <Button onClick={handleOnRemove(itemToDelete)}>{t('Delete')}</Button>
          </BoxActionsWrapper>
        }
      />
    </Card>
  );
}

SavedDocuments.propTypes = {
  savedDocuments: PropTypes.object,
  documents: PropTypes.array,
  onRemove: PropTypes.func,
  onPreview: PropTypes.func,
  onEdit: PropTypes.func
};
